import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useBulkActionsSlot } from '@/components/ListCard'

export interface BulkAction {
  label: string
  icon: string
  severity?: 'success' | 'danger' | 'warning' | 'info' | 'secondary'
  onClick: () => void
}

interface BulkActionBarProps {
  count: number
  actions?: BulkAction[]
  onClear: () => void
  loading?: boolean
  /** Convenience props — adds activate/deactivate actions automatically */
  onActivate?: () => void
  onDeactivate?: () => void
}

export function BulkActionBar({ count, actions, onClear, loading, onActivate, onDeactivate }: BulkActionBarProps) {
  const { t } = useTranslation()
  const slot = useBulkActionsSlot()

  if (count === 0) return null

  const resolvedActions: BulkAction[] = actions ?? [
    ...(onActivate ? [{ label: t('common.activate'), icon: 'check-circle', onClick: onActivate }] : []),
    ...(onDeactivate ? [{ label: t('common.deactivate'), icon: 'x-circle', onClick: onDeactivate }] : []),
  ]

  // Inside a ListCard: a single solid primary menu at the start of the
  // header, so selecting rows never pushes the table down.
  if (slot) {
    if (!slot.element) return null
    return createPortal(
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" size="sm" loading={loading}>
            {t('common.selected_count', { count })} <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {resolvedActions.map((action) => (
            <DropdownMenuItem key={action.label} onClick={action.onClick} disabled={loading}>
              <Icon name={action.icon} className="size-4" />
              {action.label}
            </DropdownMenuItem>
          ))}
          {resolvedActions.length > 0 && <DropdownMenuSeparator />}
          <DropdownMenuItem onClick={onClear}>
            <X className="size-4" />
            {t('common.clear')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>,
      slot.element
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 16px',
        background: 'var(--ad-primary-soft)',
        borderBottom: '1px solid var(--ad-border)',
        fontSize: 13,
      }}
    >
      <span style={{ fontWeight: 600, color: 'var(--ad-primary)', marginRight: 4 }}>
        {t('common.selected_count', { count })}
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm" loading={loading}>
            {t('common.actions')} <ChevronDown className="ml-1 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {resolvedActions.map((action) => (
            <DropdownMenuItem
              key={action.label}
              onClick={action.onClick}
              disabled={loading}
            >
              <Icon name={action.icon} className="size-4" />
              {action.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <div style={{ flex: 1 }} />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onClear}
      >
        <X className="mr-1 h-4 w-4" /> {t('common.clear')}
      </Button>
    </div>
  )
}
